import * as _ from 'lodash-es';
import * as styles from './getMagneticWallItemLayout.module.scss';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const MIN_COMMENT_HEIGHT = parseInt(styles.minCommentHeight, 10);
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const THREAD_SPACING = 12;

const COMPACT_POP_OVER_OFFSET = parseInt(styles.compactPopOverOffset, 10);

export enum HeightState {
  Normal,
  Focus,
}

export class VerticalSpan {
  static fromDOMRect(rect: DOMRect) {
    return new VerticalSpan(rect.top, rect.bottom);
  }

  constructor(
    public readonly top: number,
    public readonly bottom: number,
  ) {}

  get height() {
    return this.bottom - this.top;
  }

  moveTo(newTop: number) {
    return new VerticalSpan(newTop, newTop + this.height);
  }
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const CONTENT_OFFSETS: Record<ItemSize, number> = {
  compact: parseInt(styles.contentOffsetCompact, 10),
  full: parseInt(styles.contentOffsetFull, 10),
};

const byIsOverlapping = (source: VerticalSpan) => (target: VerticalSpan) =>
  target.top <= source.bottom && target.bottom >= source.top;

const getNonRestrictedTopBelow = (
  current: ItemSpan,
  restrictedSpans: VerticalSpan[],
  minTop = 0,
) => {
  const firstOverlappedIndex = restrictedSpans.findIndex(
    byIsOverlapping(current.thread),
  );
  const firstOverlapped = restrictedSpans[firstOverlappedIndex];
  if (!firstOverlapped) return current.thread.top;

  // A special case: check if it's possible to place the thread above the first restricted span
  if (firstOverlapped.top > current.content.top) {
    const previous = restrictedSpans[firstOverlappedIndex - 1];
    const topAbove =
      firstOverlapped.top - current.thread.height - THREAD_SPACING;
    if ((!previous || previous.bottom < topAbove) && topAbove >= minTop) {
      return topAbove;
    }
  }

  // Starting from the last restricted span,
  // find the first restricted span under where the thread can be placed
  const lastOverlappedIndex = restrictedSpans.findLastIndex(
    byIsOverlapping(current.thread),
  );
  for (let i = lastOverlappedIndex; i < restrictedSpans.length; i += 1) {
    const span = restrictedSpans[i];
    if (!span) break;

    const next = restrictedSpans[i + 1];
    const topBelowSpan = span.bottom + THREAD_SPACING;
    if (
      !next ||
      topBelowSpan + current.thread.height + THREAD_SPACING < next.top
    ) {
      return topBelowSpan;
    }
  }
  return current.thread.top;
};

const isMultiLineComment = (contentHeight: number, threadHeight: number) => {
  // Based on whether the content height exceeds a hardcoded ratio of
  // the thread (facepile) height.
  return contentHeight > threadHeight * 1.5;
};

export const getScrollDistanceToThreadContext = (
  scrollingContainerSpan: VerticalSpan | DOMRect,
  threadContextSpan: VerticalSpan | DOMRect,
) => {
  const SCROLLING_PADDING = scrollingContainerSpan.height * 0.05;
  if (
    threadContextSpan.height + 2 * SCROLLING_PADDING >
    scrollingContainerSpan.height
  ) {
    // Do not scroll if the element is bigger than the scrolling container
    // since scrolling will not help enough to warrant it
    return 0;
  }
  const topDiff = threadContextSpan.top - scrollingContainerSpan.top;
  if (topDiff < SCROLLING_PADDING) {
    return topDiff - SCROLLING_PADDING;
  }
  const bottomDiff = scrollingContainerSpan.bottom - threadContextSpan.bottom;
  if (bottomDiff < SCROLLING_PADDING) {
    return SCROLLING_PADDING - bottomDiff;
  }
  return 0;
};

export type CompactPopOverSide = 'top' | 'bottom';

type ItemSize = 'full' | 'compact';

export interface MagneticWallState {
  heights: Record<string, Record<HeightState, number | null>>;
  compactPopOverHeights: Record<string, number>;
  magneticWallItems: { id: string }[];

  /**
   * Spans that are restricted for threads to be placed in. These could be wide blots or post covers.
   */
  restrictedSpans: VerticalSpan[];
  contentRects: Record<string, DOMRect>;
  itemSize: ItemSize;
  focus: { id: string; active?: boolean } | null;
  scrollingContainerSpan: VerticalSpan;
}

export interface Layout {
  /**
   * The top position of each thread
   */
  tops: Record<string, number>;

  compactPopOverSide: CompactPopOverSide;

  /**
   * The minimum height of the magnetic wall to ensure all threads are visible
   */
  magneticWallMinHeight: number | null;

  /**
   * IDs of the threads that are overlapped by restricted spans
   */
  overlappedIds: Set<string>;
}

type ItemSpan = {
  id: string;
  content: VerticalSpan;
  thread: VerticalSpan;
};

function calculateTops(
  itemSpans: ItemSpan[],
  restrictedSpans: VerticalSpan[],
  priorityIndex: number,
) {
  const priority = itemSpans[priorityIndex];
  if (!priority) return {};

  const tops = { [priority.id]: priority.thread.top };

  for (let i = priorityIndex - 1; i >= 0; i -= 1) {
    const current = itemSpans[i];
    if (!current) break;

    const next = itemSpans[i + 1];
    const nextAdjustedTop = next ? tops[next.id] : null;

    const maxTopBasedOnNext =
      typeof nextAdjustedTop === 'number'
        ? nextAdjustedTop - current.thread.height - THREAD_SPACING
        : Infinity;

    let top = Math.min(current.thread.top, maxTopBasedOnNext);

    // Move the thread upper if it overlaps with any restricted span
    const firstOverlappedIndex = restrictedSpans.findIndex(
      byIsOverlapping(current.thread.moveTo(top)),
    );
    if (firstOverlappedIndex !== -1) {
      for (let j = firstOverlappedIndex; j >= 0; j -= 1) {
        const span = restrictedSpans[j];
        if (!span) break;

        const previous = restrictedSpans[j - 1];
        const topAboveSpan = span.top - current.thread.height - THREAD_SPACING;
        if (!previous || topAboveSpan > previous.bottom + THREAD_SPACING) {
          top = topAboveSpan;
          break;
        }
      }
    }

    tops[current.id] = top;
  }

  for (let i = priorityIndex + 1; i < itemSpans.length; i += 1) {
    const current = itemSpans[i];
    if (!current) break;

    const previous = itemSpans[i - 1];
    const previousAdjustedTop = previous ? tops[previous.id] : null;

    const minTopBasedOnPrevious =
      typeof previousAdjustedTop === 'number' && previous
        ? previousAdjustedTop + previous.thread.height + THREAD_SPACING
        : -Infinity;

    const top = Math.max(current.thread.top, minTopBasedOnPrevious);

    tops[current.id] = getNonRestrictedTopBelow(
      { ...current, thread: current.thread.moveTo(top) },
      restrictedSpans,
      minTopBasedOnPrevious,
    );
  }

  return tops;
}

/**
 * @param state state of the magnetic wall that is needed to calculate the layout
 * @param reserved when provided, the layout will make sure the reserved item is placed as specified,
 * and will not adjust its position
 */
function getMagneticWallItemLayout(
  state: MagneticWallState,
  reserved?: {
    top: number;
    id: string;
    compactPopOverSide: CompactPopOverSide;
  },
): Layout {
  const {
    heights,
    magneticWallItems,
    restrictedSpans,
    contentRects,
    itemSize,
    focus,
    scrollingContainerSpan,
    compactPopOverHeights,
  } = state;

  let itemSpans = _.compact(
    magneticWallItems.map((item) => {
      const contentRect = contentRects[item.id];
      return contentRect ? { id: item.id, contentRect } : null;
    }),
  )
    .toSorted(({ contentRect: rectA }, { contentRect: rectB }) =>
      rectA.top === rectB.top ? rectA.left - rectB.left : rectA.top - rectB.top,
    )
    .map(({ id, contentRect }) => {
      const threadTop = contentRect.top + CONTENT_OFFSETS[itemSize];
      const heightState =
        id === focus?.id ? HeightState.Focus : HeightState.Normal;
      const threadHeight = heights[id]?.[heightState] ?? MIN_COMMENT_HEIGHT;

      return {
        id,
        content: new VerticalSpan(contentRect.top, contentRect.bottom),
        thread: new VerticalSpan(threadTop, threadTop + threadHeight),
      };
    });

  let compactPopOverSide: CompactPopOverSide = 'top';

  if (!itemSpans[0]) {
    return {
      tops: {},
      compactPopOverSide,
      magneticWallMinHeight: null,
      overlappedIds: new Set(),
    };
  }

  let firstInViewportIndex = 0;
  const firstVisibleIndex = itemSpans.findIndex(
    (span) => span.content.bottom > scrollingContainerSpan.top,
  );
  firstInViewportIndex =
    firstVisibleIndex === -1
      ? itemSpans.length - 1 // Pick the last one if all threads are above the viewport
      : firstVisibleIndex;

  // Calculate the base tops without considering focuses or reserved items.
  // We adjust the tops for focuses and reserved items based on that
  // to ensure minimal movement of threads.
  const baseTops = calculateTops(
    _.update([...itemSpans], firstInViewportIndex, (span) => ({
      ...span,
      thread: span.thread.moveTo(
        getNonRestrictedTopBelow(span, restrictedSpans),
      ),
    })),
    restrictedSpans,
    firstInViewportIndex,
  );

  const reservedIndex = itemSpans.findIndex(({ id }) => id === reserved?.id);
  const focusedThreadIndex = itemSpans.findIndex(({ id }) => id === focus?.id);

  let priorityIndex = reservedIndex === -1 ? focusedThreadIndex : reservedIndex;
  if (priorityIndex === -1) {
    priorityIndex = firstInViewportIndex;
  }

  itemSpans = [
    ...itemSpans.slice(0, priorityIndex).map((item) => ({
      ...item,
      thread: item.thread.moveTo(baseTops[item.id] ?? item.thread.top),
    })),
    ...itemSpans.slice(priorityIndex),
  ];

  const priority = itemSpans[priorityIndex]!;
  let adjustedPriorityTop = priority.thread.top;

  if (reserved) {
    adjustedPriorityTop = reserved.top;
    compactPopOverSide = reserved.compactPopOverSide;
  } else if (focusedThreadIndex === -1) {
    adjustedPriorityTop = getNonRestrictedTopBelow(
      { ...priority, thread: priority.thread.moveTo(adjustedPriorityTop) },
      restrictedSpans,
    );
  } else {
    let { top: containerTop, bottom: containerBottom } = scrollingContainerSpan;

    // Take into account that the thread context would be scrolled into view
    const distance = getScrollDistanceToThreadContext(
      scrollingContainerSpan,
      priority.content,
    );
    containerTop += distance;
    containerBottom += distance;

    if (priority.thread.height < scrollingContainerSpan.height) {
      // Move the thread into view
      adjustedPriorityTop -= getScrollDistanceToThreadContext(
        new VerticalSpan(containerTop, containerBottom),
        priority.thread.moveTo(adjustedPriorityTop),
      );

      // If the priority thread context is between one or two restricted spans,
      // try to adjust the thread top to make it fit between them.
      const overlappedSpans = restrictedSpans.filter(
        byIsOverlapping(priority.thread.moveTo(adjustedPriorityTop)),
      );

      let hasAdjusted = false;
      const spanBelowThreadContent = overlappedSpans.find(
        ({ top }) => top >= priority.content.top,
      );
      if (spanBelowThreadContent) {
        // Try moving the thread above the overlapped span that is below the thread content
        const topAbove =
          spanBelowThreadContent.top - priority.thread.height - THREAD_SPACING;
        if (topAbove > containerTop) {
          adjustedPriorityTop = topAbove;
          hasAdjusted = true;
        }
      }
      if (!hasAdjusted) {
        const spanAboveThreadContent = overlappedSpans.findLast(
          ({ top }) => top < priority.content.top,
        );
        if (spanAboveThreadContent) {
          // Try moving the thread below the overlapped span that is above the thread content
          const topBelow = spanAboveThreadContent.bottom + THREAD_SPACING;
          if (
            topBelow + priority.thread.height + THREAD_SPACING <
            containerBottom
          ) {
            adjustedPriorityTop = topBelow;
          }
        }
      }
    }

    // Determine which side the pop over should display on
    // Spec: https://the.slab.com/posts/magnetic-wall-positioning-m29glxd0#h1w9q-tablet
    const popOverHeight = compactPopOverHeights[priority.id];
    if (
      focus?.active &&
      itemSize === 'compact' &&
      typeof popOverHeight === 'number'
    ) {
      const popOverTopOffset =
        adjustedPriorityTop - popOverHeight - COMPACT_POP_OVER_OFFSET;

      // Try other display options if the pop over is not fully visible
      // when placed above the thread
      if (popOverTopOffset < containerTop) {
        const maxTopForThread =
          containerBottom - popOverHeight - COMPACT_POP_OVER_OFFSET;

        // If the pop over's top edge is positioned above the container's top edge (i.e., it appears above the title),
        // we force it to show below the thread to ensure that the pop over can be scrolled into view:
        // https://share.slab.com/CDRdwrL8
        const shouldPlacePopOverBelow = popOverTopOffset < 0;

        const topWithBottomAlign =
          priority.content.bottom - priority.thread.height;

        if (
          (shouldPlacePopOverBelow || topWithBottomAlign < maxTopForThread) &&
          isMultiLineComment(priority.content.height, priority.thread.height)
        ) {
          compactPopOverSide = 'bottom';
          adjustedPriorityTop = topWithBottomAlign;
        } else if (
          shouldPlacePopOverBelow ||
          adjustedPriorityTop < maxTopForThread
        ) {
          compactPopOverSide = 'bottom';
        }
      }
    }
  }

  const tops = calculateTops(
    _.update([...itemSpans], priorityIndex, (span) => ({
      ...span,
      thread: span.thread.moveTo(adjustedPriorityTop),
    })),
    restrictedSpans,
    priorityIndex,
  );

  const last = itemSpans.at(-1);
  const lastTop = last && tops[last.id];
  const magneticWallMinHeight =
    last && typeof lastTop === 'number'
      ? lastTop + last.thread.height + THREAD_SPACING
      : null;

  const overlappedIds = new Set(
    itemSpans
      .filter(({ thread, id }) =>
        restrictedSpans.some(
          byIsOverlapping(thread.moveTo(tops[id] ?? -Infinity)),
        ),
      )
      .map(({ id }) => id),
  );

  return {
    tops,
    compactPopOverSide,
    magneticWallMinHeight,
    overlappedIds,
  };
}

export default getMagneticWallItemLayout;
